/**
 * @file OrderCreateCustomerCard.tsx
 * @description Карточка «Клиент» формы создания заказа (вынесено из
 * `OrderCreatePage` в phase19.24 шаг 5.1).
 *
 * Два состояния: поиск (inline-подбор + «Из списка») и выбранный клиент с
 * кнопкой «Сменить». Здесь же решается судьба e-mail: без него письмо с
 * кнопкой подтверждения отправлять некуда, поэтому адрес добавляется прямо
 * тут (F15, решение пользователя 2026-09-21) — уходить в карточку клиента и
 * терять набранный заказ менеджер не должен.
 *
 * По той же причине рядом с «Из списка» живёт «Новый клиент»: клиент, который
 * звонит впервые, в справочнике не найдётся, а уходить его заводить на
 * отдельную страницу — терять набранный заказ. Диалог тот же, что в разделе
 * «Клиенты» (`CustomerEditDialog`), поэтому правила (обязательный e-mail,
 * юр.лицо) одни на оба входа. Кнопка видна только с `SHOP_MANAGE_CUSTOMERS`.
 */

import { List, User, UserPlus, X } from "lucide-react";
import { useState } from "react";

import { CustomerEmailInlineForm } from "./CustomerEmailInlineForm";
import { useHasPermission } from "../../auth/hooks";
import { CustomerEditDialog } from "../customers/CustomerEditDialog";
import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { NoticeStrip } from "../ui/NoticeStrip";
import type { OrderCustomerPickerState } from "../../hooks/useOrderCustomerPicker";

interface Props {
  picker: OrderCustomerPickerState;
  onOpenList: () => void;
}

export function OrderCreateCustomerCard({ picker, onOpenList }: Props) {
  const customer = picker.customer;
  // Заводить клиента может только тот, кому это разрешено ролью: наблюдателю
  // кнопки не показываем, чтобы не упереться в 403 после заполнения формы.
  const canCreateCustomer = useHasPermission("SHOP_MANAGE_CUSTOMERS");
  const [creating, setCreating] = useState(false);

  return (
    <EditorialCard>
      <EditorialCard.Header
        title="Клиент"
        // На 360px группа из двух кнопок уходит под заголовок во всю ширину:
        // в колонке `auto` рядом с заголовком ей тесно и кнопки вставали друг
        // под друга, хотя вдвоём помещаются в строку (QA 2026-09-21).
        actionPlacement="below-on-mobile"
        action={
          !customer ? (
            // До 640 — одна строка из двух равных колонок, подпись кнопки
            // переносится внутри неё: «Новый клиент» на 360 уходил за край
            // карточки (QA-5 D5), а столбиком — лишняя высота. Тач-цель ≥48px.
            <div className="flex flex-wrap items-center justify-end gap-2 max-sm:grid max-sm:grid-cols-2">
              <Button
                variant="outline"
                className="min-h-12 max-sm:min-w-0 max-sm:whitespace-normal"
                onClick={onOpenList}
              >
                <List className="mr-1 size-4" />
                Из списка
              </Button>
              {canCreateCustomer ? (
                <Button
                  variant="outline"
                  className="min-h-12 max-sm:min-w-0 max-sm:whitespace-normal"
                  onClick={() => setCreating(true)}
                >
                  <UserPlus className="mr-1 size-4" />
                  Новый клиент
                </Button>
              ) : null}
            </div>
          ) : undefined
        }
      />
      <EditorialCard.Body className="flex flex-col gap-3">
        {customer ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <User className="size-5 text-primary" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{customer.displayName ?? "—"}</span>
                <span className="block truncate text-xs text-on-surface-variant">
                  {customer.email ?? customer.phone ?? "—"}
                  {customer.type === "LEGAL" ? " · Юр.лицо" : ""}
                </span>
              </span>
              <Button variant="outline" className="min-h-12" onClick={picker.clear}>
                <X className="mr-1 size-4" />
                Сменить
              </Button>
            </div>
            {customer.email ? null : (
              <>
                <NoticeStrip tone="warning">
                  У клиента нет e-mail — письмо с подтверждением отправить
                  некуда. Добавьте адрес здесь или согласуйте по телефону
                  (пункт в меню «⋯» внизу формы).
                </NoticeStrip>
                <CustomerEmailInlineForm
                  customerId={customer.id}
                  onSaved={picker.pick}
                />
              </>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="o-customer">Поиск</Label>
            <Input
              id="o-customer"
              className="min-h-12"
              value={picker.query}
              onChange={(e) => picker.setQuery(e.target.value)}
              placeholder="Имя, email или телефон (минимум 2 символа)"
            />
            {picker.options.length > 0 ? (
              <ul className="mt-2 flex flex-col divide-y divide-on-surface/8 rounded-xl bg-surface-low">
                {picker.options.map((option) => (
                  <li key={option.id}>
                    <button
                      type="button"
                      className="min-h-12 w-full px-3 py-2 text-left hover:bg-surface-lowest"
                      onClick={() => picker.pick(option)}
                    >
                      <span className="block text-sm font-medium">
                        {option.displayName ?? "—"}
                      </span>
                      <span className="block text-xs text-on-surface-variant">
                        {option.email ?? option.phone ?? "—"}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </EditorialCard.Body>

      {creating ? (
        // Созданный клиент сразу встаёт в заказ — как после инлайн-e-mail:
        // источник правды один, повторно искать его в справочнике не нужно.
        <CustomerEditDialog
          open
          customer={null}
          onClose={() => setCreating(false)}
          onSaved={(created) => {
            setCreating(false);
            picker.pick(created);
          }}
        />
      ) : null}
    </EditorialCard>
  );
}
